import { ControlledInput } from '@/components/forms/controlled-input';
import { Logo } from '@/components/logo';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { ClientOnboardingData, clientOnboardingSchema } from '@/lib/validators/client-onboarding';
import { useAuthStore } from '@/store/authStore';
import { Ionicons } from '@expo/vector-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { LinearGradient } from 'expo-linear-gradient';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { getApiErrorMessage } from '@/utils/api-error';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { KEYBOARD_BOTTOM_OFFSET } from '@/components/dashboard-scroll-view';
export default function ClientOnboardingScreen() {
  const { token } = useLocalSearchParams<{
    token: string;
  }>();

  const { setInitialPassword } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { control, handleSubmit, setValue } = useForm<ClientOnboardingData>({
    resolver: zodResolver(clientOnboardingSchema),
    defaultValues: {
      token: '',
      username: '',
      newPassword: '',
      confirmPassword: '',
      phone: '',
      address: '',
    },
  });
  useEffect(() => {
    if (!token) {
      router.push('/login');
      return;
    }
    setValue('token', token as string);
  }, [token, setValue]);
  const onSubmit = async (data: ClientOnboardingData) => {
    try {
      setIsLoading(true);
      setError(null);
      await setInitialPassword(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao configurar sua conta. Tente novamente.'));
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <ThemedView className="flex-1 items-center justify-center md:p-4">
      <SeoHead title="Configurar sua conta · Controlapag" noindex />
      <LinearGradient
        colors={['#242120', '#2d2d2d', '#242120']}
        style={StyleSheet.absoluteFillObject}
      />

      <View className="absolute right-[-40] top-[-80] hidden h-[200] w-[200] rounded-full bg-orange-500/5 md:block" />
      <View className="absolute bottom-[-100] left-[-80] hidden h-[250] w-[250] rounded-full bg-orange-500/5 md:block" />
      <View className="absolute left-[30] top-[150] hidden h-[100] w-[100] rounded-full bg-orange-500/5 md:block" />

      <View
        className={`
          md:elevation-10 mb-4 w-full flex-1 overflow-hidden
          rounded-3xl bg-card md:max-h-[90%] md:max-w-xl md:shadow-2xl
        `}
      >
        <View className="h-2 bg-primary md:hidden" />

        <View className="items-center px-4 pb-4 pt-6 md:px-5">
          <View className="mb-4">
            <Logo color="tint" />
          </View>
          <ThemedText className="my-2 text-center text-sm leading-relaxed text-foreground/70">
            Complete seu cadastro e defina sua senha
          </ThemedText>
        </View>

        <KeyboardAwareScrollView
          keyboardShouldPersistTaps="handled"
          bottomOffset={KEYBOARD_BOTTOM_OFFSET}
          className="flex-1 px-4 md:px-5"
          showsVerticalScrollIndicator={false}
        >
          <View className="gap-4 pb-4">
            <ControlledInput
              control={control}
              name="username"
              label="Nome completo"
              placeholder="João Silva"
              icon="person-outline"
            />

            <ControlledInput
              control={control}
              name="phone"
              label="Telefone"
              placeholder="(11) 98765-4321"
              keyboardType="phone-pad"
              maskType="phone"
              icon="call-outline"
            />

            <ControlledInput
              control={control}
              name="address"
              label="Endereço"
              placeholder="Rua, número, bairro, cidade"
              icon="location-outline"
            />

            <View className="my-2 h-px bg-gray-200" />

            <ControlledInput
              control={control}
              name="newPassword"
              label="Nova senha"
              placeholder="••••••••"
              secureTextEntry
              icon="lock-closed-outline"
            />

            <ControlledInput
              control={control}
              name="confirmPassword"
              label="Confirmar senha"
              placeholder="••••••••"
              secureTextEntry
              icon="lock-closed-outline"
            />

            {error && (
              <View className="flex-row items-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3">
                <Ionicons className="text-error-600" name="alert-circle" size={18} />
                <ThemedText className="flex-1 font-medium text-sm text-error-600">
                  {error}
                </ThemedText>
              </View>
            )}
          </View>
        </KeyboardAwareScrollView>

        <View className="px-4 pb-4 md:px-5">
          <Pressable
            onPress={handleSubmit(onSubmit)}
            disabled={isLoading}
            className={`items-center justify-center rounded-xl bg-orange-500 py-3.5 shadow-lg shadow-orange-500/30 ${isLoading ? 'opacity-70' : 'active:bg-orange-600'}`}
          >
            {isLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text className="font-bold text-base tracking-wide text-white">
                CONCLUIR CADASTRO
              </Text>
            )}
          </Pressable>
        </View>
      </View>
    </ThemedView>
  );
}
